<script setup lang="ts">
import {computed, provide, ref, watchEffect} from "vue";
import SideBarUtils from "@/utils/common/SideBarUtils";
import {UIMenuGroup, UIMenuItem, UIMenuSubGroup} from "@/utils/UIConstants/UIMenuGroup";
import {RouteLocationNormalized, useRoute, useRouter} from "vue-router";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {useNamaFlagsStore, useSidebarFixed, useSideBarMenuMode} from "@/stores/nama-flags-store.ts";
import PlaceTokens from "@/utils/constants/PlaceTokens.ts";
import RouteNames from "@/router/RouteNames.ts";
import SideBarAsTreeMode from "@/components/SideBarAsTreeMode.vue";
import SideBarAsDrillDownMode from "@/components/SideBarAsDrillDownMode.vue";
import {sideBarContext} from "@/utils/presenters/interfaces.ts";
import SideBarSearchField from "@/components/inputs/SideBarSearchField.vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import SideBarAsFocusedTreeMode from "@/components/SideBarAsFocusedTreeMode.vue";
import Translator from "@/utils/common/Translator.ts";

const {sidebarMenuMode} = useSideBarMenuMode();
const {sidebarFixed} = useSidebarFixed();
const originalMenuGroups: UIMenuGroup[] = SideBarUtils.fetchMenuDetails();
const panel = ref<string | undefined>("home");
let lastActivePanel;
const currentGroup = ref<UIMenuGroup | null>(null);
const currentSubGroup = ref<UIMenuSubGroup | null>(null);
const currentItem = ref<UIMenuItem | null>(null);
const searchValue = ref<string | null>(null);
let router = useRouter();
let route = useRoute();
let searchMenuGroups = computed<UIMenuGroup[]>(() => {
  let groups: UIMenuGroup[] = [];
  let value = searchValue.value;
  let subItemsCount = 0;
  if (value) {
    if (panel.value !== "searchPanel")
      lastActivePanel = panel.value;
    panel.value = "searchPanel";
    for (let originalMenuGroup of originalMenuGroups) {
      let g: UIMenuGroup = new UIMenuGroup();
      g.groupData = originalMenuGroup.groupData;
      g.subGroups = [];
      for (let subGroup of originalMenuGroup.subGroups) {
        let subG: UIMenuSubGroup = new UIMenuSubGroup();
        subG.subGroupData = subGroup.subGroupData;
        subG.subItems = [];
        for (let subItem of subGroup.subItems) {
          let itemSearchValue = (subItem as UIMenuItem).searchValue;
          if (itemSearchValue && (itemSearchValue?.toLowerCase().includes(value.toLowerCase()) || itemSearchValue?.toLowerCase().includes(StringUtils.normalizeArabic(value))
            || itemSearchValue?.toLowerCase().includes(StringUtils.normalizeArabic(StringUtils.englishToArabic(value)))
            || itemSearchValue?.toLowerCase().includes(StringUtils.arabicToEnglish(value).toLowerCase()))) {
            subG.subItems.push(subItem);
            subItemsCount++;
          }
          if (subItemsCount > 25)
            break;
        }
        if (subG.subItems.length)
          g.subGroups.push(subG);
        if (subItemsCount > 25)
          break;
      }
      if (g.subGroups.length)
        groups.push(g);
    }
  } else {
    if (lastActivePanel && !isSideBarFocusedTreeMode()) {
      panel.value = lastActivePanel;
      lastActivePanel = undefined;
    }
  }
  return groups;
})
let displayedMenuGroups = computed<UIMenuGroup[]>(() => {
  let groups: UIMenuGroup[] = originalMenuGroups;
  let value = searchValue.value;
  if (value) {
    if (panel.value !== "searchPanel")
      lastActivePanel = panel.value;
    panel.value = "searchPanel";

  } else {
    if (lastActivePanel && !isSideBarFocusedTreeMode()) {
      panel.value = lastActivePanel;
      lastActivePanel = undefined;
    }
  }
  return groups;
})

function goToEntity(item: UIMenuItem, shouldOpenInNewTab?: boolean) {
  selectItem(currentGroup.value as UIMenuGroup | null, currentSubGroup.value as UIMenuSubGroup | null, item);
  if (item.place) {
    if (shouldOpenInNewTab)
      window.open(router.resolve(item.place).href, "_blank");
    else
      router.push(item.place);
  }
}

function guess(to: RouteLocationNormalized) {
  let menu: string | undefined = to.query[PlaceTokens.PRM_MENU_HASH] as string | undefined;
  let m = SideBarUtils.findMenuItem(menu);
  if (m) {
    selectItem(m.mg, m.sg, m.item);
    return;
  }
  let maxWeight: number = -1;
  let bestMatch: { mg: UIMenuGroup, sg: UIMenuSubGroup, item: UIMenuItem } | null = null;

  let LvEv = [RouteNames.EDIT_VIEW_ROUTE, RouteNames.LIST_VIEW_ROUTE];
  for (let mg of originalMenuGroups) {
    for (let sg of mg.subGroups) {
      for (let item of sg.subItems) {
        if (!item.place)
          continue;
        let w = -1;
        let pl = item.place as any;
        let editOrList = LvEv.includes(pl.name) && LvEv.includes(to.name as string);
        if (!editOrList && (pl.name !== to.name))
          continue;
        if (pl.name === to.name)
          w += 5;
        if (!pl.query && !to.query) {
          w += 1;
          if (w > maxWeight) {
            maxWeight = w;
            bestMatch = {mg, sg, item};
          }
          continue;
        }
        if (pl.query[PlaceTokens.PRM_ENTITY_TYPE]) {
          if (pl.query[PlaceTokens.PRM_ENTITY_TYPE] !== to.query[PlaceTokens.PRM_ENTITY_TYPE])
            continue;
          w += 10;
        }
        if (pl.query[PlaceTokens.PRM_LIST_VIEW_NAME] && pl.query[PlaceTokens.PRM_LIST_VIEW_NAME] === to.query[PlaceTokens.PRM_LIST_VIEW_NAME])
          w += 1;
        if (pl.query[PlaceTokens.PRM_EDIT_VIEW_NAME] && pl.query[PlaceTokens.PRM_EDIT_VIEW_NAME] === to.query[PlaceTokens.PRM_EDIT_VIEW_NAME])
          w += 1;
        if (pl.query[PlaceTokens.PRM_EXTRA_CRITERIA] && pl.query[PlaceTokens.PRM_EXTRA_CRITERIA] === to.query[PlaceTokens.PRM_EXTRA_CRITERIA])
          w += 1;
        if (pl.query[PlaceTokens.PRM_ID] && pl.query[PlaceTokens.PRM_ID] === to.query[PlaceTokens.PRM_ID])
          w += 1;
        if (w > maxWeight) {
          maxWeight = w;
          bestMatch = {mg, sg, item};
        }
      }
    }
  }
  if (bestMatch)
    selectItem(bestMatch.mg, bestMatch.sg, bestMatch.item);
}

function isSideBarFocusedTreeMode() {
  return sidebarMenuMode.value.value == NamaCommon.sideBarMenuModes.FOCUSED_TREE_MODE;
}

function selectItem(mg: UIMenuGroup | null, sg: UIMenuSubGroup | null, item: UIMenuItem) {
  if (!item)
    return;
  currentGroup.value = mg;
  currentSubGroup.value = sg;
  currentItem.value = item;
  if (sg && sg.subGroupData.code) {
    if (searchValue.value)
      lastActivePanel = sg.subGroupData.code;
    else if (isSideBarFocusedTreeMode())
      panel.value = mg?.groupData.code;
    else
      panel.value = sg.subGroupData.code;
  }

  setTimeout(() => {
    let elementById = document.getElementById(item.code!);
    if (!elementById)
      elementById = document.getElementById(item.id!);
    if (!elementById)
      return;
    elementById.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest'
    });
  }, 500);
}

watchEffect(() => guess(route));

function isCurrent(item: UIMenuItem) {
  if (!item)
    return false;
  if (!currentItem.value)
    return false;
  if (item.code && currentItem.value.code === item.code) {
    return true;
  }
  if (item.id && currentItem.value.id === item.id) {
    return true;
  }
  return false;
}

function mainGroupClick(group: UIMenuGroup) {
  if (searchValue.value)
    searchValue.value = '';
  currentGroup.value = group;
  currentSubGroup.value = null;
  panel.value = group.groupData.code!;
}

function onSubGroupClicked(subGroup: UIMenuSubGroup) {
  if (searchValue.value)
    searchValue.value = '';
  currentSubGroup.value = subGroup;
  panel.value = subGroup.subGroupData.code!;
}

function getSideBarMenuModeOptions() {
  const values = Object.values(NamaCommon.sideBarMenuModes);
  return values.map(o => ({label: Translator.translateId(o), value: o}))
}

provide(sideBarContext, {
  isCurrent,
  goToEntity,
  mainGroupClick,
  onSubGroupClicked,
  searchValue,
  searchMenuGroups,
  displayedMenuGroups,
  currentGroup,
  currentSubGroup,
  panel
})
</script>
<template>
  <div class="tw-h-full tw-flex tw-flex-col dark:tw-text-namaFontColor-dark dark:tw-bg-namaBackground-deep-dark">
    <div class="tw-w-full tw-h-full">
      <SideBarAsTreeMode v-if="sidebarMenuMode.value == NamaCommon.sideBarMenuModes.TREE_MODE">
        <SideBarSearchField v-model="searchValue" id="sideBarSearchInput"/>
      </SideBarAsTreeMode>
      <SideBarAsDrillDownMode v-else-if="sidebarMenuMode.value == NamaCommon.sideBarMenuModes.DRILL_DOWN_MODE">
        <SideBarSearchField v-model="searchValue" id="sideBarSearchInput"/>
      </SideBarAsDrillDownMode>
      <SideBarAsFocusedTreeMode v-else>
        <SideBarSearchField v-model="searchValue" id="sideBarSearchInput"/>
      </SideBarAsFocusedTreeMode>
    </div>
    <div class="tw-flex tw-border-t tw-gap-2 tw-p-2 dark:tw-border-white/10 tw-border-black/10 tw-items-center tw-w-full tw-shrink-0">
      <q-toggle class="!tw-text-sm tw-shrink-0" :label="Translator.translateId('pin')" v-model="sidebarFixed"/>
      <q-select
        outlined dense :options-dense="useNamaFlagsStore().compactMode"
        v-model="useNamaFlagsStore().sidebarMenuMode"
        :placeholder="Translator.translateId('sideBarMenuMode')"
        :label="Translator.translateId('sideBarMenuMode')"
        :options="getSideBarMenuModeOptions()"
        class="tw-flex-grow tw-min-w-0 nama-criteria-field-input nama-customize-list-filter-dense-input"
        input-class="tw-bg-transparent tw-px-0 focus:tw-ring-0 focus:tw-shadow-none focus:tw-border-none dark:tw-text-white
              dark:tw-placeholder-gray-300 header-search-in-popup-input"/>
    </div>
  </div>
</template>
